import React, {
 useMemo, useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Plus, Trash2, ChevronDown, ChevronRight, AlertCircle, Info
} from 'lucide-react';
import {
 cn
} from 'shared/lib/utils';
import V2StepDispatcher from '../V2StepDispatcher';
import {
 OELContext
} from '../components/OELCodeEditor';
import {
    ParallelStep, JsonOpcodeStep, FlowDefinition, StepKind
} from 'builders/builderOpcode/jsonMode/types';
import {
    CALL_STEP_AUTHORING_ENABLED
} from '../featureFlags';

/**
 * V2StepParallel — recursive nested step rail for parallel branches.
 * Per A-0012.02 SPEC §2.6 + A-0008.03 §2.2.
 *
 * Each branch is itself a Step (any kind, including nested parallel).
 * Branch IDs validated unique within the block (red-highlight inline).
 * UI disables when/for/repeat_until on parallel-kind step itself
 * (those go on individual branches).
 */
const KIND_OPTIONS: { value: StepKind; label: string }[] = [
    {
 value: 'llm',
label: 'LLM'
},
    {
 value: 'rag',
label: 'RAG'
},
    {
 value: 'api',
label: 'API'
},
    {
 value: 'tool',
label: 'Tool'
},
    {
 value: 'memory',
label: 'Memory'
},
    {
 value: 'choose',
label: 'Choose'
},
    {
 value: 'subflow',
label: 'SubFlow'
},
    {
 value: 'parallel',
label: 'Parallel (nested)'
},
    ...(CALL_STEP_AUTHORING_ENABLED ? [{
 value: 'call' as StepKind,
label: 'Call'
}] : [])
];

function makeBranchId(existingIds: string[]) {
    let i = existingIds.length + 1;
    while (existingIds.includes(`branch_${i}`)) i++;
    return `branch_${i}`;
}

function emptyStep(kind: StepKind, id: string): JsonOpcodeStep {
    const base: any = {
 id,
kind
};
    switch (kind) {
        case 'llm': return {
 ...base,
llm: {
 unique_name: '',
model_type: ''
},
inputs: {
 query: ''
},
output_type: 'text'
};
        case 'rag': return {
 ...base,
operation: 'basic_search',
inputs: {
 collection_id: '',
query: '',
top_k: 4
}
};
        case 'api': return {
 ...base,
api_id: '',
inputs: {
 path_params: '',
query_params: '',
body: '',
headers: ''
}
};
        case 'tool': return {
 ...base,
protocol: 'mcp',
tool_ref: {
 connection_id: '',
tool_name: ''
},
inputs: {}
};
        case 'memory': return {
 ...base,
mode: 'read',
turns: 8,
max_tokens: 2000
};
        case 'choose': return {
 ...base,
choose: {
 cases: [],
default: {
 kind: 'step',
ref: ''
}
}
};
        case 'subflow': return {
 ...base,
subflow: {
 ref: ''
},
with: {}
};
        case 'parallel': return {
 ...base,
branches: []
};
        case 'call': return {
 ...base,
call: {
 ref: ''
},
inputs: {}
};
        default: return base;
    }
}

const V2StepParallel = ({
    step,
    onStepChange,
    definitions = [],
    flows = [],
    oelContext,
}: {
    step: ParallelStep;
    onStepChange: (step: ParallelStep) => void;
    definitions?: JsonOpcodeStep[];
    flows?: FlowDefinition[];
    oelContext?: OELContext;
}) => {
    const ctx: OELContext = oelContext || {
 inputs: [],
previousStepIds: []
};
    const branches = step.branches || [];
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [pickKind, setPickKind] = useState<StepKind | ''>('');

    // Validate branch IDs unique within this block
    const idCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        branches.forEach((b: any) => {
            const id = b?.id || '';
            counts[id] = (counts[id] || 0) + 1;
        });
        return counts;
    }, [branches]);

    function addBranch(kind: StepKind) {
        if (!kind) return;
        const ids = branches.map((b: any) => b.id || '');
        const id = makeBranchId(ids);
        const newBranch = emptyStep(kind, id);
        onStepChange({
 ...step,
branches: [...branches, newBranch]
});
        setExpanded((e) => ({
 ...e,
[id]: true
}));
        setPickKind('');
    }

    function removeBranch(idx: number) {
        const next = branches.filter((_, i) => i !== idx);
        onStepChange({
 ...step,
branches: next
});
    }

    function updateBranch(idx: number, updated: JsonOpcodeStep) {
        const next = [...branches];
        next[idx] = updated;
        onStepChange({
 ...step,
branches: next
});
    }

    function moveBranch(idx: number, dir: -1 | 1) {
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= branches.length) return;
        const next = [...branches];
        [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
        onStepChange({
 ...step,
branches: next
});
    }

    function tog(id: string) {
        setExpanded((e) => ({
 ...e,
[id]: !e[id]
}));
    }

    return (
        <div className="space-y-3">
            <div className="rounded-md border border-blue-200 bg-blue-50/50 p-2 text-[11px] text-blue-900">
                <Info className="mr-1 inline h-3 w-3" />
                <b>Parallel</b> runs all branches concurrently and collects results.
                Per A-0008.03 §2.2: <code>when</code>/<code>for</code>/<code>repeat_until</code>
                are NOT supported on the parallel step itself — set them on individual branches.
            </div>

            <div>
                <Label>Branches ({branches.length})</Label>
                <div className="mt-2 space-y-2">
                    {branches.length === 0 && (
                        <div className="rounded-md border border-dashed bg-muted/20 p-4 text-center text-sm text-muted-foreground">
                            No branches yet. Add one below.
                        </div>
                    )}
                    {branches.map((branch: any, i) => {
                        const id = branch.id || '';
                        const isDup = idCounts[id] > 1;
                        const open = !!expanded[id];
                        return (
                            <div key={`${id}-${i}`} className={cn(
                                'overflow-hidden rounded-md border bg-card',
                                isDup && 'border-red-400'
                            )}>
                                <div className="flex items-center gap-2 border-b bg-muted/30 px-2 py-1.5">
                                    <button type="button" onClick={() => tog(id)} className="text-muted-foreground">
                                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                                        {branch.kind}
                                    </span>
                                    <Input
                                        value={id}
                                        onChange={(e) => updateBranch(i, {
 ...branch,
id: e.target.value
})}
                                        placeholder="branch_id"
                                        className={cn('h-7 flex-1 font-mono text-[12.5px]', isDup && 'border-red-400')}
                                    />
                                    {isDup && (
                                        <span title="Duplicate branch ID" className="text-red-500">
                                            <AlertCircle className="h-4 w-4" />
                                        </span>
                                    )}
                                    <Button type="button" variant="ghost" size="sm"
                                            onClick={() => moveBranch(i, -1)} disabled={i === 0}>↑</Button>
                                    <Button type="button" variant="ghost" size="sm"
                                            onClick={() => moveBranch(i, 1)} disabled={i === branches.length - 1}>↓</Button>
                                    <Button type="button" variant="ghost" size="sm"
                                            className="text-destructive" onClick={() => removeBranch(i)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                                {open && (
                                    <div className="border-t p-3">
                                        <V2StepDispatcher
                                            step={branch}
                                            onStepChange={(updated) => updateBranch(i, updated)}
                                            definitions={definitions}
                                            flows={flows}
                                            oelContext={ctx}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex gap-2">
                <Select value={pickKind} onValueChange={(v) => setPickKind(v as StepKind)}>
                    <SelectTrigger className="w-44">
                        <SelectValue placeholder="Pick a kind…" />
                    </SelectTrigger>
                    <SelectContent>
                        {KIND_OPTIONS.map((k) => (
                            <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={() => pickKind && addBranch(pickKind as StepKind)} disabled={!pickKind}>
                    <Plus className="mr-1 h-4 w-4" /> Add branch
                </Button>
            </div>

            {Object.values(idCounts).some((c) => c > 1) && (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
                    <AlertCircle className="mr-1 inline h-4 w-4" />
                    Branch IDs must be unique. Fix duplicates above before saving.
                </div>
            )}
        </div>
    );
};

export default V2StepParallel;
